import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Languages, MessageSquare, Smile, Sparkles, Star, ThumbsUp, Trash2, X } from 'lucide-react';
import { Avatar } from '@/components/Avatar';
import { getTheme } from '@/lib/themes';
import {
    checkComment,
    createComment,
    deletePost,
    getPostComments,
    getPostLikes,
    getPoll,
    likePost,
    toggleSelfSpotlight,
    toggleSpotlight,
    undoComment,
    uploadFile,
    votePoll,
} from '@/lib/api';
import { TatemaeModal } from './TatemaeModal';
import { TranslateModal } from './TranslateModal';
import { getImageFileFromClipboard, insertTextAtCursor } from '@/utils/pasteImage';
import { autoResizeTextarea } from '@/utils/autoResize';
import { useI18n } from '@/lib/i18n';
import { getInputSurface, getMutedTextColor, isDarkTheme, pickTextColor, withAlpha } from '@/lib/themeStyles';

import { useNavigate, useOutletContext } from 'react-router-dom';
import { FloorTheme } from '@/lib/themes';

interface PostProps {
    post: {
        post_id: number;
        user_id: number;
        content: string;
        created_at: string;
        likes: number;
        image_path?: string;
        post_type?: 'text' | 'image' | 'video' | 'poll';
        poll_id?: number | null;
        last_comment_at?: string | null;
        like_entries?: any[];
        user: {
            username: string;
            display_name?: string;
            floor_level: number;
            role_type: string;
            avatar_path?: string;
        };
    };
    currentUserId?: number;
    currentUser?: {
        user_id: number;
        role_type: string;
        floor_level: number;
    } | null;
    spotlightPostId?: number | null;
    selfSpotlightPostId?: number | null;
    onSpotlightChange?: () => void;
    onDeleted?: () => void;
    onScoreChange?: () => void;
}

const EMOJI_CHOICES = ['❤️', '✨', '👍', '🔥', '👏', '😍', '🎉', '🌸', '💎', '🥂', '😎', '💡', '😊', '🌟', '🙌', '🕊️', '🍀', '☕️'];
const MAX_EMOJI_PREVIEW = 5;

export function PostCard({
    post,
    currentUserId,
    currentUser,
    spotlightPostId,
    selfSpotlightPostId,
    onSpotlightChange,
    onDeleted,
    onScoreChange,
}: PostProps) {
    const resolveName = (displayName?: string) => {
        if (displayName && displayName.trim()) return displayName;
        return t('住人');
    };
    const parseServerDate = (value: string) => {
        if (!value) return null;
        const normalized = value.includes('T') ? value : value.replace(' ', 'T');
        const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized);
        const iso = hasZone ? normalized : `${normalized}Z`;
        const parsed = new Date(iso);
        if (!Number.isNaN(parsed.getTime())) return parsed;
        const fallback = new Date(value);
        return Number.isNaN(fallback.getTime()) ? null : fallback;
    };
    const formatDateTime = (value: string) => {
        const parsed = parseServerDate(value);
        return parsed ? parsed.toLocaleString() : '';
    };
    const date = formatDateTime(post.created_at);
    const { theme } = useOutletContext<{ theme: FloorTheme }>();
    const navigate = useNavigate();
    const { t } = useI18n();
    const darkTheme = isDarkTheme(theme);
    const mutedTextColor = getMutedTextColor(theme);
    const inputSurface = getInputSurface(theme);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [likes, setLikes] = useState<any[]>(post.like_entries || []);
    const [likesLoading, setLikesLoading] = useState(false);
    const [showLikesModal, setShowLikesModal] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [selectedEmoji, setSelectedEmoji] = useState('');
    const [showComments, setShowComments] = useState(false);
    const [comments, setComments] = useState<any[]>([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [commentFocused, setCommentFocused] = useState(false);
    const commentInputRef = useRef<HTMLTextAreaElement>(null);
    const [commentChecking, setCommentChecking] = useState(false);
    const [commentSuggestion, setCommentSuggestion] = useState<any>(null);
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [commentError, setCommentError] = useState('');
    const [commentSubmitting, setCommentSubmitting] = useState(false);
    const [undoingCommentId, setUndoingCommentId] = useState<number | null>(null);
    const [pollData, setPollData] = useState<any>(null);
    const [pollLoading, setPollLoading] = useState(false);
    const [pollError, setPollError] = useState('');
    const [selectedPollOption, setSelectedPollOption] = useState<number | null>(null);
    const [showPollConfirm, setShowPollConfirm] = useState(false);
    const [pollSubmitting, setPollSubmitting] = useState(false);
    const [hasNewComments, setHasNewComments] = useState(false);
    const [translateOpen, setTranslateOpen] = useState(false);
    const [translateSource, setTranslateSource] = useState('');

    const commentDraftKey = currentUserId
        ? `maison_post_comment_draft_${currentUserId}_${post.post_id}`
        : null;

    useEffect(() => {
        if (!commentDraftKey) return;
        const saved = localStorage.getItem(commentDraftKey);
        if (saved && !commentText) {
            setCommentText(saved);
        }
    }, [commentDraftKey]);

    useEffect(() => {
        if (!commentDraftKey) return;
        if (commentText) {
            localStorage.setItem(commentDraftKey, commentText);
        } else {
            localStorage.removeItem(commentDraftKey);
        }
    }, [commentDraftKey, commentText]);

    useEffect(() => {
        autoResizeTextarea(commentInputRef.current);
    }, [commentText]);

    const markCommentsSeen = (timestamp?: string | null) => {
        if (!commentSeenKey) return;
        const value = timestamp || new Date().toISOString();
        localStorage.setItem(commentSeenKey, value);
        setHasNewComments(false);
    };

    const isOwner = currentUserId && post.user_id === currentUserId;
    const isPoll = post.post_type === 'poll' && !!post.poll_id;
    const showPollResults = !!pollData && (!!pollData.my_vote_option_id || pollData.user_id === currentUserId);
    const pollTotalVotes = pollData?.total_votes || 0;

    const formatFloorLabel = (roleType?: string, floorLevel?: number) => {
        if (roleType && ['Admin', 'Manager', 'Maestro'].includes(roleType)) return '∞';
        if (typeof floorLevel === 'number') return String(floorLevel);
        return '-';
    };
    const isStaffViewer = currentUser ? ['Admin', 'Maestro', 'Manager'].includes(currentUser.role_type) : false;
    const postTextColor = (() => {
        if (currentUser?.role_type === 'Admin') return theme.colors.primary;
        if (!isStaffViewer && currentUser?.floor_level === 1) return '#ffffff';
        if (!isStaffViewer && currentUser?.floor_level === 2) return theme.colors.text;
        return '';
    })();
    const headerTextColor = currentUser?.role_type === 'Admin' ? theme.colors.primary : '';
    const isStaff = currentUser ? ['Admin', 'Maestro', 'Manager'].includes(currentUser.role_type) : false;
    const canEmoji = isStaff || (currentUser?.floor_level ?? 0) >= 4;
    const canSpotlight = !isStaff && (currentUser?.floor_level ?? 0) >= 6 && !isOwner;
    const canSelfSpotlight = !isStaff && (currentUser?.floor_level ?? 0) >= 8 && !!isOwner;
    const isSpotlighted = spotlightPostId === post.post_id;
    const isSelfSpotlighted = selfSpotlightPostId === post.post_id;
    const commentSeenKey = currentUserId ? `maison_post_comment_seen_${currentUserId}_${post.post_id}` : null;

    const likedByMe = !!likes.find((like) => like.user_id === currentUserId);
    const emojiLikes = likes.filter((like) => like.emoji).map((like) => like.emoji as string);
    const emojiPreview = emojiLikes.slice(0, MAX_EMOJI_PREVIEW);
    const hasMoreEmojis = emojiLikes.length > MAX_EMOJI_PREVIEW;
    const hasCommented = !!comments.find((comment) => comment.user_id === currentUserId);

    const renderPortal = (node: ReactNode) => {
        if (typeof document === 'undefined') return null;
        return createPortal(node, document.body);
    };

    const fetchPoll = async () => {
        if (!isPoll || !post.poll_id) return;
        setPollLoading(true);
        setPollError('');
        try {
            const res = await getPoll(post.poll_id);
            setPollData(res.data);
        } catch (err: any) {
            setPollError(err?.response?.data?.detail || 'アンケートの読み込みに失敗しました。');
        } finally {
            setPollLoading(false);
        }
    };

    const fetchLikes = async () => {
        setLikesLoading(true);
        try {
            const res = await getPostLikes(post.post_id);
            setLikes(res.data || []);
        } catch (err) {
            console.error('Failed to load likes', err);
        } finally {
            setLikesLoading(false);
        }
    };

    const fetchComments = async () => {
        setCommentsLoading(true);
        try {
            const res = await getPostComments(post.post_id);
            const nextComments = res.data || [];
            setComments(nextComments);
            const latest = nextComments.length ? nextComments[nextComments.length - 1]?.created_at : null;
            if (latest) {
                markCommentsSeen(latest);
            }
        } catch (err) {
            console.error('Failed to load comments', err);
        } finally {
            setCommentsLoading(false);
        }
    };

    useEffect(() => {
        if (post.like_entries !== undefined) {
            setLikes(post.like_entries);
            setLikesLoading(false);
            return;
        }
        fetchLikes();
    }, [post.post_id, post.like_entries]);

    useEffect(() => {
        if (showComments) {
            fetchComments();
        }
    }, [showComments]);

    useEffect(() => {
        if (!isPoll) return;
        fetchPoll();
    }, [isPoll, post.poll_id]);

    useEffect(() => {
        if (!commentSeenKey) {
            setHasNewComments(false);
            return;
        }
        const lastCommentAt = post.last_comment_at;
        if (!lastCommentAt) {
            setHasNewComments(false);
            return;
        }
        const lastCommentDate = parseServerDate(lastCommentAt);
        if (!lastCommentDate) {
            setHasNewComments(false);
            return;
        }
        const seenRaw = localStorage.getItem(commentSeenKey);
        if (!seenRaw) {
            setHasNewComments(true);
            return;
        }
        const seenDate = parseServerDate(seenRaw);
        if (!seenDate) {
            setHasNewComments(true);
            return;
        }
        setHasNewComments(lastCommentDate > seenDate);
    }, [commentSeenKey, post.last_comment_at]);

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await deletePost(post.post_id);
            setShowDeleteConfirm(false);
            if (onDeleted) onDeleted();
        } catch (err) {
            console.error('Failed to delete post', err);
            alert('削除に失敗しました。');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleLike = async (emoji?: string) => {
        if (isOwner) return;
        if (likedByMe && !emoji) return;
        try {
            await likePost(post.post_id, emoji);
            await fetchLikes();
            if (onScoreChange) onScoreChange();
        } catch (err: any) {
            console.error('Failed to like post', err);
            alert(err?.response?.data?.detail || 'いいねに失敗しました。');
        }
    };

    const handleCommentSubmit = async () => {
        const trimmed = commentText.trim();
        if (!trimmed) {
            setCommentError('コメントを入力してください。');
            return;
        }
        setCommentChecking(true);
        setCommentError('');
        try {
            const checkRes = await checkComment(post.post_id, trimmed);
            const data = checkRes.data;
            if (data?.error) {
                setCommentError(`AI校正に失敗しました: ${data.error}`);
                return;
            }
            if (data.is_rewritten) {
                setCommentSuggestion(data);
                setShowCommentModal(true);
                if (onScoreChange) onScoreChange();
                return;
            }
            await submitComment(trimmed);
        } catch (err: any) {
            setCommentError(err?.response?.data?.detail || 'コメントの検閲に失敗しました。');
        } finally {
            setCommentChecking(false);
        }
    };

    const submitComment = async (content: string) => {
        setCommentSubmitting(true);
        setCommentError('');
        try {
            await createComment(post.post_id, content);
            setCommentText('');
            if (commentDraftKey) {
                localStorage.removeItem(commentDraftKey);
            }
            await fetchComments();
            if (onScoreChange) onScoreChange();
        } catch (err: any) {
            setCommentError(err?.response?.data?.detail || 'コメントの投稿に失敗しました。');
        } finally {
            setCommentSubmitting(false);
        }
    };

    const handlePasteImageToComment = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const imageFile = getImageFileFromClipboard(e);
        if (!imageFile) return;
        e.preventDefault();
        if (!imageFile.type.startsWith('image/')) return;
        const special = ['Admin', 'Manager', 'Maestro'].includes(currentUser?.role_type || '');
        const canImage = special || (currentUser?.floor_level ?? 0) >= 5;
        if (!canImage) {
            alert('画像の貼り付けはFloor 5から解放されます。');
            return;
        }
        try {
            const res = await uploadFile(imageFile);
            const url = res.data?.url;
            if (url) {
                insertTextAtCursor(e.currentTarget, commentText, `\n${url}\n`, setCommentText);
            }
        } catch (err) {
            console.error('Paste upload failed', err);
            setCommentError('画像の貼り付けに失敗しました。');
        }
    };

    const handleCommentAccept = async () => {
        if (!commentSuggestion?.converted) return;
        setCommentChecking(true);
        try {
            await submitComment(commentSuggestion.converted);
            setShowCommentModal(false);
            setCommentSuggestion(null);
        } finally {
            setCommentChecking(false);
        }
    };

    const formatUndoWindow = (seconds: number | null | undefined) => {
        if (typeof seconds !== 'number' || seconds <= 0) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${String(secs).padStart(2, '0')}`;
    };

    const handleUndoComment = async (commentId: number) => {
        if (undoingCommentId !== null) return;
        setUndoingCommentId(commentId);
        setCommentError('');
        try {
            await undoComment(post.post_id, commentId);
            await fetchComments();
            if (onScoreChange) onScoreChange();
        } catch (err: any) {
            setCommentError(err?.response?.data?.detail || 'コメントの取り消しに失敗しました。');
        } finally {
            setUndoingCommentId(null);
        }
    };

    const handleNavigate = (event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;
        if (commentFocused || commentText.trim()) return;
        if (target.closest('[data-stop-profile-nav]')) {
            return;
        }
        navigate(`/users/${post.user_id}`);
    };

    const handleUserNavigate = (userId: number) => {
        setShowLikesModal(false);
        navigate(`/users/${userId}`);
    };

    const handleSpotlight = async () => {
        if (!canSpotlight) return;
        try {
            await toggleSpotlight(post.post_id);
            if (onSpotlightChange) onSpotlightChange();
        } catch (err: any) {
            alert(err?.response?.data?.detail || 'スポットライトの更新に失敗しました。');
        }
    };

    const handleSelfSpotlight = async () => {
        if (!canSelfSpotlight) return;
        try {
            await toggleSelfSpotlight(post.post_id);
            if (onSpotlightChange) onSpotlightChange();
        } catch (err: any) {
            alert(err?.response?.data?.detail || 'セルフスポットライトの更新に失敗しました。');
        }
    };

    const confirmPollVote = async () => {
        if (!pollData || !selectedPollOption) return;
        setPollSubmitting(true);
        try {
            await votePoll(pollData.poll_id, selectedPollOption);
            setShowPollConfirm(false);
            setSelectedPollOption(null);
            await fetchPoll();
            if (onScoreChange) onScoreChange();
        } catch (err: any) {
            alert(err?.response?.data?.detail || '投票に失敗しました。');
        } finally {
            setPollSubmitting(false);
        }
    };

    const isRoundedFull = theme.radius === 'rounded-full';
    const cardPaddingClass = isRoundedFull ? 'px-6 pt-10 pb-10 sm:p-6' : 'p-6';
    const cardRadiusClass = isRoundedFull ? 'rounded-[32px] sm:rounded-full' : theme.radius;

    return (
        <div
            className={`${cardPaddingClass} border mb-4 transition-all hover:shadow-lg cursor-pointer ${cardRadiusClass} ${theme.shadow} overflow-hidden`}
            style={{
                borderColor: theme.colors.border,
                backgroundColor: withAlpha(theme.colors.card, darkTheme ? 0.92 : 0.8),
                color: theme.colors.text,
            }}
            role="button"
            tabIndex={0}
            onClick={handleNavigate}
            onKeyDown={(event) => {
                const target = event.target as HTMLElement;
                if (commentFocused || commentText.trim()) return;
                if (target.closest('textarea, input, [contenteditable="true"]')) return;
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(`/users/${post.user_id}`);
                }
            }}
        >
            <div className="flex items-start justify-between mb-4 gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <Avatar
                        username={resolveName(post.user.display_name)}
                        src={post.user.avatar_path}
                        size="md"
                        theme={getTheme(post.user.role_type, post.user.floor_level)}
                    />
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                            <h4
                                className="font-bold truncate"
                                style={headerTextColor ? { color: headerTextColor } : { color: theme.colors.text }}
                            >
                                {resolveName(post.user.display_name)}
                            </h4>
                            <span
                                className="text-xs px-2 py-0.5 rounded-full"
                                style={{
                                    backgroundColor: withAlpha(theme.colors.primary, darkTheme ? 0.18 : 0.1),
                                    color: theme.colors.primary,
                                }}
                            >
                                F{formatFloorLabel(post.user.role_type, post.user.floor_level)}
                            </span>
                        </div>
                        <span className="text-xs block" style={{ color: mutedTextColor }}>{date}</span>
                    </div>
                </div>
                {isOwner && (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowDeleteConfirm(true)}
                        className="hover:text-red-500"
                        style={{ color: mutedTextColor }}
                        title="削除"
                        data-stop-profile-nav
                    >
                        <Trash2 size={18} />
                    </Button>
                )}
            </div>

            {!isPoll && (
                <div
                    className="text-gray-800 mb-4 whitespace-pre-wrap break-words leading-relaxed"
                    style={postTextColor ? { color: postTextColor } : undefined}
                >
                    {post.content}
                </div>
            )}

            {isPoll && (
                <div className="mb-4 space-y-3" data-stop-profile-nav>
                    {pollLoading ? (
                        <div className="text-xs" style={{ color: mutedTextColor }}>アンケートを読み込み中...</div>
                    ) : pollError ? (
                        <div className="text-xs text-red-500">{pollError}</div>
                    ) : pollData ? (
                        <>
                            <div className="font-semibold whitespace-pre-wrap" style={{ color: theme.colors.text }}>
                                {pollData.question}
                            </div>
                            <div className="space-y-2">
                                {pollData.options.map((option: any) => {
                                    const isSelected = selectedPollOption === option.option_id;
                                    const isVoted = pollData.my_vote_option_id === option.option_id;
                                    const percent = pollTotalVotes
                                        ? Math.round((option.vote_count / pollTotalVotes) * 100)
                                        : 0;
                                    return (
                                        <label
                                            key={option.option_id}
                                            className="block border rounded-md p-3 text-sm space-y-2"
                                            style={{
                                                borderColor: isVoted ? theme.colors.primary : theme.colors.border,
                                                backgroundColor: isVoted
                                                    ? withAlpha(theme.colors.primary, darkTheme ? 0.12 : 0.06)
                                                    : withAlpha(theme.colors.card, darkTheme ? 0.76 : 0.52),
                                            }}
                                        >
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    name={`poll-${post.post_id}`}
                                                    checked={isSelected}
                                                    onChange={() => setSelectedPollOption(option.option_id)}
                                                    disabled={!pollData.can_vote}
                                                />
                                                <span className="flex-1">{option.text}</span>
                                                {showPollResults && (
                                                    <span className="text-xs" style={{ color: mutedTextColor }}>{option.vote_count}票</span>
                                                )}
                                            </div>
                                            {showPollResults && (
                                                <div
                                                    className="h-2 rounded-full overflow-hidden"
                                                    style={{ backgroundColor: withAlpha(theme.colors.primary, darkTheme ? 0.16 : 0.08) }}
                                                >
                                                    <div
                                                        className="h-full"
                                                        style={{
                                                            backgroundColor: withAlpha(theme.colors.primary, 0.72),
                                                            width: `${percent}%`,
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                            <div className="flex items-center justify-between text-xs" style={{ color: mutedTextColor }}>
                                <span>合計 {pollTotalVotes} 票</span>
                                {pollData.visibility && (
                                    <span>
                                        公開範囲: {pollData.visibility === 'public'
                                            ? '全体'
                                            : pollData.visibility === 'friends'
                                                ? 'フレンドのみ'
                                                : '指定フレンドのみ'}
                                    </span>
                                )}
                            </div>
                            {pollData.can_vote && (
                                <div className="flex items-center justify-between">
                                    <div className="text-xs" style={{ color: mutedTextColor }}>投票は1回のみで取り消しできません。</div>
                                    <Button
                                        size="sm"
                                        style={{ backgroundColor: theme.colors.primary, color: pickTextColor(theme.colors.primary) }}
                                        disabled={!selectedPollOption || pollSubmitting}
                                        onClick={() => setShowPollConfirm(true)}
                                    >
                                        投票する
                                    </Button>
                                </div>
                            )}
                            {!pollData.can_vote && pollData.my_vote_option_id && (
                                <div className="text-xs" style={{ color: mutedTextColor }}>投票済みです。</div>
                            )}
                        </>
                    ) : null}
                </div>
            )}

            {post.image_path && (
                <div
                    className="mb-4 rounded-lg overflow-hidden border"
                    style={{
                        backgroundColor: withAlpha(theme.colors.secondary, darkTheme ? 0.54 : 0.18),
                        borderColor: withAlpha(theme.colors.border, 0.7),
                    }}
                    data-stop-profile-nav
                >
                    {post.post_type === 'video' ? (
                        <video
                            src={post.image_path}
                            controls
                            className="w-full max-h-[500px]"
                            preload="metadata"
                        />
                    ) : (
                        <img
                            src={post.image_path}
                            alt="Attached media"
                            className="w-full h-auto object-cover max-h-[600px]"
                            loading="lazy"
                        />
                    )}
                </div>
            )}

            <div className="flex flex-wrap items-center gap-4 text-sm w-full" style={{ color: mutedTextColor }}>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2 cursor-pointer hover:text-primary"
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            if (canEmoji) {
                                setShowEmojiPicker(true);
                            } else {
                                handleLike();
                            }
                        }}
                        disabled={isOwner || (!canEmoji && likedByMe)}
                        data-stop-profile-nav
                    >
                        <ThumbsUp size={16} />
                        {t('いいね')}
                    </Button>
                    <button
                        type="button"
                        className="flex items-center hover:text-primary"
                        style={{ color: mutedTextColor }}
                        onClick={(event) => {
                            event.stopPropagation();
                            setShowLikesModal(true);
                        }}
                        data-stop-profile-nav
                        aria-label="いいね一覧"
                    >
                        <Smile size={18} />
                    </button>
                    <button
                        type="button"
                        className="flex items-center border-0 bg-transparent p-0 text-inherit"
                        onClick={(event) => {
                            event.stopPropagation();
                            setShowLikesModal(true);
                        }}
                        data-stop-profile-nav
                        aria-label="絵文字リアクション一覧"
                    >
                        {emojiPreview.map((emoji, index) => (
                            <span
                                key={`${emoji}-${index}`}
                                className={`text-base ${index > 0 ? '-ml-2' : ''}`}
                            >
                                {emoji}
                            </span>
                        ))}
                        {hasMoreEmojis && <span className="text-xs -ml-2">…</span>}
                    </button>
                </div>

                <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2 cursor-pointer hover:text-primary"
                    onClick={() => {
                        setShowComments((prev) => {
                            const next = !prev;
                            if (next) {
                                setTimeout(() => commentInputRef.current?.focus(), 0);
                            }
                            return next;
                        });
                        if (showComments) {
                            setTimeout(() => commentInputRef.current?.focus(), 0);
                        }
                    }}
                    data-stop-profile-nav
                >
                    <MessageSquare size={16} />
                    {t('コメント')}
                    {hasNewComments && (
                        <span
                            className="ml-1 h-2 w-2 rounded-full"
                            style={{ backgroundColor: theme.colors.primary }}
                            aria-label="新着コメント"
                            data-stop-profile-nav
                        />
                    )}
                </Button>

                {canSpotlight && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2"
                        style={{ color: isSpotlighted ? theme.colors.primary : mutedTextColor }}
                        onClick={handleSpotlight}
                        data-stop-profile-nav
                    >
                        <Star size={16} />
                        {t('スポットライト')}
                    </Button>
                )}

                {canSelfSpotlight && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2"
                        style={{ color: isSelfSpotlighted ? theme.colors.primary : mutedTextColor }}
                        onClick={handleSelfSpotlight}
                        data-stop-profile-nav
                    >
                        <Sparkles size={16} />
                        {t('セルフスポット')}
                    </Button>
                )}
                {post.content && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2 cursor-pointer hover:text-primary"
                        onClick={() => {
                            setTranslateSource(post.content || '');
                            setTranslateOpen(true);
                        }}
                        data-stop-profile-nav
                    >
                        <Languages size={16} />
                        {t('翻訳')}
                    </Button>
                )}
            </div>

            {showComments && (
                <div className="mt-4 space-y-4" data-stop-profile-nav>
                    {commentsLoading ? (
                        <div className="text-xs" style={{ color: mutedTextColor }}>読み込み中...</div>
                    ) : comments.length === 0 ? (
                        <div className="text-xs" style={{ color: mutedTextColor }}>{t('コメントはまだありません。')}</div>
                    ) : (
                        <div className="space-y-3">
                            {comments.map((comment) => (
                                <div key={comment.comment_id} className="flex gap-3 text-sm">
                                    <button
                                        onClick={() => handleUserNavigate(comment.user_id)}
                                        className="shrink-0"
                                        data-stop-profile-nav
                                    >
                                        <Avatar
                                            username={resolveName(comment.display_name)}
                                            src={comment.avatar_path}
                                            size="sm"
                                            theme={getTheme(comment.role_type || 'User', comment.floor_level)}
                                        />
                                    </button>
                                    <div className="flex-1">
                                        <button
                                            onClick={() => handleUserNavigate(comment.user_id)}
                                            className="font-semibold hover:underline"
                                            style={{ color: theme.colors.text }}
                                            data-stop-profile-nav
                                        >
                                            {resolveName(comment.display_name)}
                                        </button>
                                        <div className="text-xs flex flex-wrap items-center gap-2" style={{ color: mutedTextColor }}>
                                            <span>{formatDateTime(comment.created_at)}</span>
                                            {comment.is_mine && comment.can_undo && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleUndoComment(comment.comment_id)}
                                                    disabled={undoingCommentId === comment.comment_id}
                                                    className="text-[11px] font-semibold hover:underline disabled:opacity-50"
                                                    style={{ color: theme.colors.primary }}
                                                    data-stop-profile-nav
                                                >
                                                    {undoingCommentId === comment.comment_id
                                                        ? '取り消し中...'
                                                        : `取り消す (${formatUndoWindow(comment.seconds_until_undo_expire)})`}
                                                </button>
                                            )}
                                        </div>
                                        <div className="whitespace-pre-wrap mt-1" style={{ color: theme.colors.text }}>{comment.content}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {commentError && (
                        <div className="text-xs text-red-500">{commentError}</div>
                    )}

                    <div className="border-t pt-3">
                        {isOwner ? (
                            <div className="text-xs" style={{ color: mutedTextColor }}>{t('自分の投稿にはコメントできません。')}</div>
                        ) : hasCommented ? (
                            <div className="text-xs" style={{ color: mutedTextColor }}>{t('コメントは各投稿1回までです。')}</div>
                        ) : (
                            <>
                                <textarea
                                    ref={commentInputRef}
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    onInput={(e) => autoResizeTextarea(e.currentTarget)}
                                    onPaste={handlePasteImageToComment}
                                    rows={3}
                                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 resize-none text-[16px] md:text-sm"
                                    style={{
                                        borderColor: theme.colors.border,
                                        backgroundColor: inputSurface,
                                        color: theme.colors.text,
                                    }}
                                    placeholder={t('コメントを入力...')}
                                    onFocus={() => setCommentFocused(true)}
                                    onBlur={() => setCommentFocused(false)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
                                            e.stopPropagation();
                                        }
                                        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                                            e.preventDefault();
                                            handleCommentSubmit();
                                        }
                                    }}
                                />
                                <div className="flex items-center justify-between mt-2">
                                    <div className="text-xs" style={{ color: mutedTextColor }}>{t('Enterで改行 / Ctrl+Enterで送信')}</div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={handleCommentSubmit}
                                            disabled={commentChecking || commentSubmitting}
                                            className="gap-2"
                                            style={{ backgroundColor: theme.colors.primary, color: pickTextColor(theme.colors.primary) }}
                                        >
                                            {commentChecking ? t('検閲中...') : t('コメントする')}
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <TatemaeModal
                isOpen={showCommentModal && !!commentSuggestion}
                data={commentSuggestion}
                onClose={() => {
                    setShowCommentModal(false);
                    setCommentSuggestion(null);
                }}
                onAccept={handleCommentAccept}
                onSelfEdit={() => {
                    setShowCommentModal(false);
                    if (commentSuggestion?.converted) {
                        setCommentText(commentSuggestion.converted);
                    }
                    setCommentSuggestion(null);
                }}
            />

            {showPollConfirm && renderPortal(
                <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div
                        className="rounded-lg shadow-xl max-w-md w-full border-2 p-6 space-y-4"
                        style={{
                            backgroundColor: withAlpha(theme.colors.card, darkTheme ? 0.96 : 0.94),
                            borderColor: theme.colors.primary,
                            color: theme.colors.text,
                        }}
                    >
                        <h3 className="text-lg font-bold" style={{ color: theme.colors.primary }}>投票の確認</h3>
                        <p className="text-sm" style={{ color: mutedTextColor }}>
                            この内容で投票しますか？投票は1回のみで、取り消しはできません。
                        </p>
                        <div className="flex justify-center gap-3 pt-2">
                            <Button
                                onClick={confirmPollVote}
                                disabled={pollSubmitting}
                                className="px-6"
                                style={{ backgroundColor: theme.colors.primary, color: pickTextColor(theme.colors.primary) }}
                            >
                                {pollSubmitting ? '送信中...' : '投票する'}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => setShowPollConfirm(false)}
                                className="px-6"
                            >
                                戻る
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {showLikesModal && renderPortal(
                <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div
                        className="rounded-lg shadow-xl max-w-md w-full border-2 p-6 space-y-4"
                        style={{
                            backgroundColor: withAlpha(theme.colors.card, darkTheme ? 0.96 : 0.94),
                            borderColor: theme.colors.primary,
                            color: theme.colors.text,
                        }}
                    >
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold" style={{ color: theme.colors.primary }}>{t('いいね一覧')}</h3>
                            <button
                                onClick={() => setShowLikesModal(false)}
                                className="hover:text-gray-600"
                                style={{ color: mutedTextColor }}
                                data-stop-profile-nav
                            >
                                <X size={18} />
                            </button>
                        </div>
                        {likesLoading ? (
                            <div className="text-sm" style={{ color: mutedTextColor }}>読み込み中...</div>
                        ) : likes.length === 0 ? (
                            <div className="text-sm" style={{ color: mutedTextColor }}>{t('まだいいねはありません。')}</div>
                        ) : (
                            <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
                                {likes.map((like) => (
                                    <button
                                        key={like.like_id}
                                        onClick={() => handleUserNavigate(like.user_id)}
                                        className="w-full flex items-center gap-3 text-left p-2 rounded-md"
                                        style={{ backgroundColor: withAlpha(theme.colors.secondary, darkTheme ? 0.14 : 0.08) }}
                                        data-stop-profile-nav
                                    >
                                        <Avatar
                                            username={resolveName(like.display_name)}
                                            src={like.avatar_path}
                                            size="sm"
                                            theme={getTheme(like.role_type || 'User', like.floor_level)}
                                        />
                                        <div className="flex-1">
                                            <div className="font-semibold" style={{ color: theme.colors.text }}>
                                                {resolveName(like.display_name)}
                                            </div>
                                            <div className="text-xs" style={{ color: mutedTextColor }}>F{formatFloorLabel(like.role_type, like.floor_level)}</div>
                                        </div>
                                        {like.emoji && <span className="text-lg">{like.emoji}</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showEmojiPicker && renderPortal(
                <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div
                        className="rounded-lg shadow-xl max-w-md w-full border-2 p-6 space-y-4"
                        style={{
                            backgroundColor: withAlpha(theme.colors.card, darkTheme ? 0.96 : 0.94),
                            borderColor: theme.colors.primary,
                            color: theme.colors.text,
                        }}
                    >
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold" style={{ color: theme.colors.primary }}>{t('絵文字いいね')}</h3>
                            <button
                                onClick={() => setShowEmojiPicker(false)}
                                className="hover:text-gray-600"
                                style={{ color: mutedTextColor }}
                                data-stop-profile-nav
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="grid grid-cols-6 gap-2">
                            {EMOJI_CHOICES.map((emoji) => (
                                <button
                                    key={emoji}
                                    className="text-xl border rounded-md p-2"
                                    style={{
                                        borderColor: selectedEmoji === emoji ? theme.colors.primary : theme.colors.border,
                                        backgroundColor: selectedEmoji === emoji
                                            ? withAlpha(theme.colors.primary, darkTheme ? 0.16 : 0.08)
                                            : withAlpha(theme.colors.card, darkTheme ? 0.72 : 0.52),
                                    }}
                                    onClick={() => setSelectedEmoji(emoji)}
                                    data-stop-profile-nav
                                >
                                    {emoji}
                                </button>
                            ))}
                        </div>
                        <input
                            value={selectedEmoji}
                            onChange={(e) => setSelectedEmoji(e.target.value.trim())}
                            placeholder={t('自分で入力（1文字）')}
                            className="w-full border rounded-md p-2 text-sm"
                            style={{
                                borderColor: theme.colors.border,
                                backgroundColor: inputSurface,
                                color: theme.colors.text,
                            }}
                        />
                        <div className="flex justify-center gap-3 pt-2">
                            <Button
                                onClick={async () => {
                                    if (!selectedEmoji) return;
                                    await handleLike(selectedEmoji);
                                    setShowEmojiPicker(false);
                                    setSelectedEmoji('');
                                }}
                                className="px-6"
                                style={{ backgroundColor: theme.colors.primary, color: pickTextColor(theme.colors.primary) }}
                            >
                                {t('送る')}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowEmojiPicker(false);
                                    setSelectedEmoji('');
                                }}
                                className="px-6"
                            >
                                {t('戻る')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteConfirm && renderPortal(
                <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div
                        className="rounded-lg shadow-xl max-w-md w-full border-2 p-6 space-y-4"
                        style={{
                            backgroundColor: withAlpha(theme.colors.card, darkTheme ? 0.96 : 0.94),
                            borderColor: theme.colors.primary,
                            color: theme.colors.text,
                        }}
                    >
                        <h3 className="text-lg font-bold" style={{ color: theme.colors.primary }}>{t('投稿を削除してもよろしいですか？')}</h3>
                        <p className="text-sm" style={{ color: mutedTextColor }}>
                            {t('削除してもポイントや投稿可能数は変わりませんのでご安心ください。')}
                        </p>
                        <div className="flex justify-center gap-3 pt-2">
                            <Button
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className="px-6"
                                style={{ backgroundColor: theme.colors.primary, color: pickTextColor(theme.colors.primary) }}
                            >
                                {isDeleting ? t('削除中...') : t('削除する')}
                            </Button>
                            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="px-6">
                                {t('戻る')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            <TranslateModal
                isOpen={translateOpen}
                onClose={() => setTranslateOpen(false)}
                sourceText={translateSource}
            />
        </div>
    );
}
